import { useParams } from 'react-router';

import { useError } from '@client/hooks/useError';
import { useTenantQueries } from '@client/queries/useTenantQueries';
import { useLoading } from '@client/store/loading.store';
import { EErrorType, type ITenant, type TErrorData } from '@common/types';

import type { AxiosResponse } from 'axios';

export const useApiKeys = () => {
  const {
    useApiKeys,
    useActivateApiKey,
    useDeactivateApiKey,
    useDeleteApiKey,
    useGenerateApiKey,
  } = useTenantQueries();
  const { setError } = useError();
  const params = useParams();

  const tenantId = params.tenantId as string;

  // Query for API keys
  const {
    data: apiKeys,
    refetch: refetchApiKeys,
    isLoading,
    isFetching,
    isError,
    error,
  } = useApiKeys(tenantId);
  const { loading, setLoading } = useLoading(isLoading && isFetching);

  // Mutations
  const activateApiKeyMutation = useActivateApiKey();
  const deactivateApiKeyMutation = useDeactivateApiKey();
  const deleteApiKeyMutation = useDeleteApiKey();
  const generateApiKeyMutation = useGenerateApiKey();

  const onGenerateApiKey = async (
    tenant: ITenant,
    expiresAt: Date | null,
  ): Promise<AxiosResponse<{ apiKey: string; apiKeyId: string }>> => {
    try {
      setLoading(true);
      const result = await generateApiKeyMutation.mutateAsync({
        tenant,
        expiresAt,
      });
      await refetchApiKeys();
      return result;
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_GENERATE_API,
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onActivateApiKey = async (apiKeyId: string): Promise<void> => {
    try {
      setLoading(true);
      await activateApiKeyMutation.mutateAsync({ tenantId, apiKeyId });
      await refetchApiKeys();
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onDeactivateApiKey = async (apiKeyId: string): Promise<void> => {
    try {
      setLoading(true);
      await deactivateApiKeyMutation.mutateAsync({ tenantId, apiKeyId });
      await refetchApiKeys();
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onDeleteApiKey = async (apiKeyId: string): Promise<void> => {
    try {
      setLoading(true);
      await deleteApiKeyMutation.mutateAsync({ tenantId, apiKeyId });
      await refetchApiKeys();
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    isError,
    error,
    onGenerateApiKey,
    onActivateApiKey,
    onDeactivateApiKey,
    onDeleteApiKey,
    refetch: refetchApiKeys,
    apiKeys,
  };
};
